import React, { useState, useEffect } from 'react';
import {
    ArrowLeft, Eye, Users, MessageSquare, Calendar, Copy, CheckCircle2,
    Activity, Printer, Phone, Clock, Loader2, Share2, Link2, Send, X, Plus, Trash2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Property, VisitComment } from '../types/types';
import { clsx } from 'clsx';
import { supabase } from '../lib/supabase';
import { PropertyNotesSection } from '../components/property/PropertyNotesSection';
import {
    createPropertyReport, buildReportUrl, copyToClipboard,
    openWhatsApp, openEmail, openSystemShare, isWebShareSupported, prepareWhatsAppWindow,
    fetchVisitComments, addVisitComment, deleteVisitComment,
} from '../lib/report-queries';

/** Format a Date as the value a datetime-local input expects (local time). */
function toLocalDatetimeValue(d: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const TYPE_META: Record<string, { label: string; color: string; bg: string }> = {
    call: { label: 'Llamada', color: 'text-blue-500', bg: 'bg-blue-500/10' },
    whatsapp: { label: 'WhatsApp', color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
    email: { label: 'Email', color: 'text-amber-500', bg: 'bg-amber-500/10' },
    visit: { label: 'Visita', color: 'text-indigo-500', bg: 'bg-indigo-500/10' },
    meeting: { label: 'Reunión', color: 'text-purple-500', bg: 'bg-purple-500/10' },
    note: { label: 'Nota', color: 'text-slate-500', bg: 'bg-slate-500/10' },
    other: { label: 'Otro', color: 'text-slate-500', bg: 'bg-slate-500/10' },
};

const OUTCOME_META: Record<string, { label: string; color: string }> = {
    sent: { label: 'Enviada', color: 'text-blue-500' },
    visit_scheduled: { label: 'Visita Programada', color: 'text-amber-500' },
    visit_done: { label: 'Visita Realizada', color: 'text-indigo-500' },
    interested: { label: 'Interesado', color: 'text-emerald-500' },
    not_interested: { label: 'No Interesado', color: 'text-rose-500' },
    reserved: { label: 'Reservada', color: 'text-purple-500' },
    sold: { label: 'Vendida', color: 'text-emerald-600' },
};

function timeAgo(dateStr: string): string {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Ahora';
    if (mins < 60) return `Hace ${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `Hace ${hrs}h`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `Hace ${days}d`;
    if (days < 30) return `Hace ${Math.floor(days / 7)} sem.`;
    return new Date(dateStr).toLocaleDateString('es-GT', { day: '2-digit', month: 'short', year: 'numeric' });
}

interface ClientReportViewProps {
    property: Property;
    theme: 'light' | 'dark' | 'high-contrast';
    onBack: () => void;
}

export const ClientReportView: React.FC<ClientReportViewProps> = ({ property, theme, onBack }) => {
    const isLight = theme !== 'dark';
    const [copied, setCopied] = React.useState(false);
    const navigate = useNavigate();

    const [seguimientos, setSeguimientos] = useState<any[]>([]);
    const [seguimientosTotal, setSeguimientosTotal] = useState(0);
    const [contactProperties, setContactProperties] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    // Share panel: generate the PUBLIC tokenized report link the owner can open
    // without logging in (the old "copy link" pointed at the auth-only CRM route).
    const [shareOpen, setShareOpen] = useState(false);
    const [message, setMessage] = useState('');
    const [generating, setGenerating] = useState(false);
    const [reportUrl, setReportUrl] = useState<string | null>(null);
    const [expiresAt, setExpiresAt] = useState<string | null>(null);
    const [shareError, setShareError] = useState<string | null>(null);

    // Comentarios de Visita — owner-facing visit feedback (shown on the report)
    const [visitComments, setVisitComments] = useState<VisitComment[]>([]);
    const [vcText, setVcText] = useState('');
    const [vcDateTime, setVcDateTime] = useState(() => toLocalDatetimeValue(new Date()));
    const [vcSaving, setVcSaving] = useState(false);
    const [vcError, setVcError] = useState<string | null>(null);

    useEffect(() => {
        if (!supabase || !property.id) return;
        let cancelled = false;

        async function load() {
            // Fetch contacts linked to this property via contact_properties
            const { data: cpData } = await supabase!
                .from('contact_properties')
                .select('*, contacts(id, name, phone, email, is_lead)')
                .eq('property_id', property.id)
                // Buyerless sale records (contact_id NULL, from Registrar
                // Venta without a comprador) are not "interesados".
                .not('contact_id', 'is', null)
                .order('created_at', { ascending: false });

            // Get all contact IDs linked to this property
            const contactIds = (cpData || []).map((cp: any) => cp.contact_id).filter(Boolean);

            // Fetch seguimientos for those contacts (list capped at 50 — the
            // stat uses an exact count so it never under-reports)
            let segData: any[] = [];
            let segTotal = 0;
            if (contactIds.length > 0) {
                const { data } = await supabase!
                    .from('seguimientos')
                    .select('*, contacts(name)')
                    .in('contact_id', contactIds)
                    .order('note_date', { ascending: false })
                    .limit(50);
                segData = data || [];
                const { count } = await supabase!
                    .from('seguimientos')
                    .select('id', { count: 'exact', head: true })
                    .in('contact_id', contactIds);
                segTotal = count ?? 0;
            }

            // Owner-facing visit comments
            let vcData: VisitComment[] = [];
            try { vcData = await fetchVisitComments(String(property.id)); } catch { /* non-fatal */ }

            if (!cancelled) {
                setContactProperties(cpData || []);
                setSeguimientos(segData);
                setSeguimientosTotal(segTotal);
                setVisitComments(vcData);
                setIsLoading(false);
            }
        }

        load();
        return () => { cancelled = true; };
    }, [property.id]);

    // Open the share panel; generate the link on first open if we don't have one.
    const handleOpenShare = () => {
        setShareOpen(true);
        if (!reportUrl) void generateLink();
    };

    // Create/refresh the public report token and build the shareable URL.
    const generateLink = async (): Promise<string | null> => {
        if (!property.id) return null;
        setGenerating(true);
        setShareError(null);
        try {
            const result = await createPropertyReport(String(property.id), message.trim() || undefined);
            const url = buildReportUrl(result.id);
            setReportUrl(url);
            setExpiresAt(result.expires_at);
            return url;
        } catch (err: any) {
            setShareError(err?.message || 'No se pudo generar el enlace.');
            return null;
        } finally {
            setGenerating(false);
        }
    };

    const shareText = `Le comparto el reporte de actividad de su propiedad "${property.title}":`;

    const handleCopyLink = async () => {
        const url = reportUrl || (await generateLink());
        if (!url) return;
        await copyToClipboard(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleWhatsApp = async () => {
        const preWindow = prepareWhatsAppWindow();
        const url = reportUrl || (await generateLink());
        if (!url) { if (preWindow && !preWindow.closed) preWindow.close(); return; }
        openWhatsApp(`*Reporte en tiempo real del inmueble*\n\n${shareText}\n${url}`, preWindow);
    };

    const handleEmail = async () => {
        const url = reportUrl || (await generateLink());
        if (!url) return;
        openEmail(`Reporte de su propiedad: ${property.title}`, `${shareText}\n\n${url}`);
    };

    const handleSystemShare = async () => {
        const url = reportUrl || (await generateLink());
        if (!url) return;
        await openSystemShare(`Reporte: ${property.title}`, shareText, url);
    };

    const handleAddComment = async () => {
        const text = vcText.trim();
        if (!text || !property.id) return;
        setVcSaving(true);
        setVcError(null);
        try {
            const isoVisited = vcDateTime ? new Date(vcDateTime).toISOString() : undefined;
            const created = await addVisitComment(String(property.id), text, isoVisited);
            // Re-insert keeping the list sorted by visited_at desc.
            setVisitComments(prev => [created, ...prev].sort((a, b) => +new Date(b.visited_at) - +new Date(a.visited_at)));
            setVcText('');
            setVcDateTime(toLocalDatetimeValue(new Date()));
        } catch (err: any) {
            setVcError(err?.message || 'No se pudo guardar el comentario.');
        } finally {
            setVcSaving(false);
        }
    };

    const handleDeleteComment = async (id: string) => {
        const prev = visitComments;
        setVisitComments(prev.filter(c => c.id !== id)); // optimistic
        try {
            await deleteVisitComment(id);
        } catch {
            setVisitComments(prev); // rollback on failure
        }
    };

    const panelCls = clsx(
        'rounded-2xl border p-6',
        isLight ? 'bg-white border-slate-100 shadow-sm' : 'bg-white/5 border-white/5'
    );

    const daysOnMarket = property.created_at ? Math.floor((Date.now() - new Date(property.created_at).getTime()) / 86400000) : 0;
    const visitCount = seguimientos.filter(s => s.type === 'visit').length;
    // Distinct prospectos (is_lead contacts) assigned to the property — same
    // rule as the public report RPC (migration 20260819180000).
    const prospectosCount = new Set(
        contactProperties.filter((cp: any) => cp.contacts?.is_lead).map((cp: any) => cp.contact_id)
    ).size;

    return (
        <div className="animate-page-entry relative space-y-8">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <button
                        onClick={onBack}
                        className={clsx(
                            'p-2.5 rounded-xl border transition-all',
                            isLight ? 'bg-white border-slate-200 text-slate-600 hover:text-slate-900' : 'bg-white/5 border-white/5 text-slate-400 hover:text-white'
                        )}
                    >
                        <ArrowLeft size={18} />
                    </button>
                    <div>
                        <h2 className={clsx("text-2xl font-black mb-1", isLight ? "text-slate-900" : "text-white")}>
                            Reporte Cliente
                        </h2>
                        <p className="text-sm text-slate-500 flex items-center gap-2">
                            <span>{property.title}</span>
                            <span className="w-1 h-1 rounded-full bg-slate-400" />
                            <span className="font-mono text-xs">{property.ii_id || `#${property.id?.slice(0, 8)}`}</span>
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2 sm:gap-3">
                    <button
                        onClick={() => navigate(`/propiedades/${property.id}/imprimir`)}
                        className={clsx(
                            "flex items-center gap-2 p-2.5 sm:px-4 sm:py-2.5 rounded-xl text-sm font-bold transition-all border",
                            isLight ? "bg-white border-slate-200 text-slate-600 hover:shadow-sm" : "bg-white/5 border-white/10 text-slate-300 hover:bg-white/10"
                        )}
                    >
                        <Printer size={16} /> <span className="hidden sm:inline">Ficha Imprimible</span>
                    </button>
                    <button
                        onClick={handleOpenShare}
                        className={clsx(
                            "flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all",
                            shareOpen
                                ? "bg-indigo-700 text-white shadow-lg shadow-indigo-600/20"
                                : "bg-indigo-600 text-white shadow-lg shadow-indigo-600/20 hover:bg-indigo-700"
                        )}
                    >
                        <Share2 size={16} />
                        <span className="hidden sm:inline">Compartir con propietario</span>
                    </button>
                </div>
            </div>

            {/* Share panel — generates the PUBLIC link the owner opens without logging in */}
            {shareOpen && (
                <div className={clsx(
                    "rounded-2xl border p-5 sm:p-6 space-y-4",
                    isLight ? "bg-indigo-50/50 border-indigo-100" : "bg-indigo-500/5 border-indigo-500/20"
                )}>
                    <div className="flex items-start justify-between gap-4">
                        <div>
                            <h3 className={clsx("text-base font-bold flex items-center gap-2", isLight ? "text-slate-900" : "text-white")}>
                                <Link2 size={16} className="text-indigo-500" /> Enlace para el propietario
                            </h3>
                            <p className="text-xs text-slate-500 mt-1">
                                Versión pública y resumida: métricas y actividad, sin datos de contacto de los interesados ni notas internas.
                            </p>
                        </div>
                        <button
                            onClick={() => setShareOpen(false)}
                            className={clsx("p-1.5 rounded-lg transition-colors", isLight ? "text-slate-400 hover:bg-slate-100" : "text-slate-500 hover:bg-white/10")}
                            aria-label="Cerrar"
                        >
                            <X size={16} />
                        </button>
                    </div>

                    {/* Optional personal message */}
                    <div>
                        <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Mensaje para el propietario (opcional)</label>
                        <textarea
                            value={message}
                            onChange={e => setMessage(e.target.value)}
                            rows={2}
                            placeholder="Estimado/a propietario/a, le comparto el avance de la comercialización de su propiedad…"
                            className={clsx(
                                "mt-1.5 w-full rounded-xl border px-3 py-2.5 text-sm resize-none outline-none transition-colors",
                                isLight
                                    ? "bg-white border-slate-200 text-slate-800 focus:border-indigo-400"
                                    : "bg-white/5 border-white/10 text-white focus:border-indigo-400 placeholder:text-slate-500"
                            )}
                        />
                    </div>

                    {/* Link row */}
                    <div className="flex flex-col sm:flex-row gap-2">
                        <div className={clsx(
                            "flex-1 flex items-center gap-2 rounded-xl border px-3 py-2.5 text-sm min-w-0",
                            isLight ? "bg-white border-slate-200" : "bg-white/5 border-white/10"
                        )}>
                            {generating ? (
                                <span className="flex items-center gap-2 text-slate-400"><Loader2 size={14} className="animate-spin" /> Generando enlace…</span>
                            ) : reportUrl ? (
                                <span className={clsx("truncate font-mono text-xs", isLight ? "text-slate-600" : "text-slate-300")}>{reportUrl}</span>
                            ) : (
                                <span className="text-slate-400 text-xs">El enlace aparecerá aquí</span>
                            )}
                        </div>
                        <button
                            onClick={() => void handleCopyLink()}
                            disabled={generating}
                            className={clsx(
                                "flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all disabled:opacity-50",
                                copied ? "bg-emerald-500 text-white" : "bg-indigo-600 text-white hover:bg-indigo-700"
                            )}
                        >
                            {copied ? <CheckCircle2 size={16} /> : <Copy size={16} />}
                            {copied ? 'Copiado' : 'Copiar'}
                        </button>
                    </div>

                    {shareError && <p className="text-xs text-rose-500">{shareError}</p>}

                    {/* Share channels */}
                    <div className="flex flex-wrap items-center gap-2">
                        <button
                            onClick={() => void handleWhatsApp()}
                            disabled={generating}
                            className="flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-bold bg-emerald-500 text-white hover:bg-emerald-600 transition-colors disabled:opacity-50"
                        >
                            <Phone size={15} /> WhatsApp
                        </button>
                        <button
                            onClick={() => void handleEmail()}
                            disabled={generating}
                            className={clsx(
                                "flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-bold transition-colors border disabled:opacity-50",
                                isLight ? "bg-white border-slate-200 text-slate-700 hover:bg-slate-50" : "bg-white/5 border-white/10 text-slate-200 hover:bg-white/10"
                            )}
                        >
                            <Send size={15} /> Correo
                        </button>
                        {isWebShareSupported() && (
                            <button
                                onClick={() => void handleSystemShare()}
                                disabled={generating}
                                className={clsx(
                                    "flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-bold transition-colors border disabled:opacity-50",
                                    isLight ? "bg-white border-slate-200 text-slate-700 hover:bg-slate-50" : "bg-white/5 border-white/10 text-slate-200 hover:bg-white/10"
                                )}
                            >
                                <Share2 size={15} /> Compartir…
                            </button>
                        )}
                        {message.trim() && reportUrl && (
                            <button
                                onClick={() => void generateLink()}
                                disabled={generating}
                                className="flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-bold text-indigo-600 hover:bg-indigo-100/60 transition-colors disabled:opacity-50"
                            >
                                Actualizar mensaje
                            </button>
                        )}
                    </div>

                    {expiresAt && (
                        <p className="text-[11px] text-slate-400 flex items-center gap-1.5">
                            <Clock size={11} /> El enlace expira el {new Date(expiresAt).toLocaleDateString('es-GT', { day: '2-digit', month: 'long', year: 'numeric' })}.
                        </p>
                    )}
                </div>
            )}

            {/* Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4 sm:gap-6">
                {[
                    { icon: Eye, label: 'Vistas Web', value: property.page_visits || 0, color: 'text-blue-500', bg: 'bg-blue-500/10' },
                    { icon: Users, label: 'Prospectos Interesados', value: prospectosCount, color: 'text-indigo-500', bg: 'bg-indigo-500/10' },
                    { icon: Calendar, label: 'Visitas', value: visitCount, color: 'text-purple-500', bg: 'bg-purple-500/10' },
                    { icon: MessageSquare, label: 'Seguimientos', value: seguimientosTotal, color: 'text-amber-500', bg: 'bg-amber-500/10' },
                    { icon: Activity, label: 'Días en Mercado', value: daysOnMarket, color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
                ].map(({ icon: Icon, label, value, color, bg }) => (
                    <div key={label} className={clsx("stagger-item flex flex-col gap-3", panelCls)}>
                        <div className={clsx("p-2.5 rounded-xl w-fit", bg, color)}>
                            <Icon size={20} />
                        </div>
                        <div>
                            <span className={clsx("text-3xl font-black block mb-1", isLight ? "text-slate-900" : "text-white")}>{value}</span>
                            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">{label}</span>
                        </div>
                    </div>
                ))}
            </div>

            {/* Observaciones — owner-report notes logged on the property detail view
                (note_type='owner_report'). Read-only here; added/edited from the detail
                view. Shows an empty state when there are none. */}
            <PropertyNotesSection
                propertyId={String(property.id)}
                theme={theme}
                readOnly
                onlyType="owner_report"
                title="Observaciones"
            />

            {/* Comentarios de Visita — owner-facing feedback shown on the report */}
            <div className={clsx("space-y-4", panelCls)}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3">
                        <div className={clsx("p-2 rounded-lg", isLight ? "bg-emerald-50 text-emerald-600" : "bg-emerald-500/10 text-emerald-400")}>
                            <MessageSquare size={18} />
                        </div>
                        <h3 className={clsx("text-lg font-bold", isLight ? "text-slate-900" : "text-white")}>
                            Comentarios de Visita ({visitComments.length})
                        </h3>
                    </div>
                    <span className={clsx("text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-md", isLight ? "bg-emerald-50 text-emerald-600" : "bg-emerald-500/10 text-emerald-400")}>
                        Visible para el propietario
                    </span>
                </div>

                {/* Add form */}
                <div className="flex flex-col gap-2">
                    <div className="flex flex-col sm:flex-row gap-2">
                        <div className="sm:w-56 shrink-0">
                            <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Fecha y hora</label>
                            <input
                                type="datetime-local"
                                value={vcDateTime}
                                onChange={e => setVcDateTime(e.target.value)}
                                className={clsx(
                                    "mt-1 w-full rounded-xl border px-3 py-2.5 text-sm outline-none transition-colors",
                                    isLight ? "bg-white border-slate-200 text-slate-800 focus:border-emerald-400" : "bg-white/5 border-white/10 text-white focus:border-emerald-400"
                                )}
                            />
                        </div>
                        <div className="flex-1">
                            <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Comentario de la visita</label>
                            <textarea
                                value={vcText}
                                onChange={e => setVcText(e.target.value)}
                                rows={2}
                                placeholder="Ej. Pareja interesada, les encantó la terraza. Consultarán financiamiento y vuelven la próxima semana."
                                className={clsx(
                                    "mt-1 w-full rounded-xl border px-3 py-2.5 text-sm resize-none outline-none transition-colors",
                                    isLight ? "bg-white border-slate-200 text-slate-800 focus:border-emerald-400" : "bg-white/5 border-white/10 text-white focus:border-emerald-400 placeholder:text-slate-500"
                                )}
                            />
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => void handleAddComment()}
                            disabled={vcSaving || !vcText.trim()}
                            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold bg-emerald-600 text-white hover:bg-emerald-700 transition-colors disabled:opacity-40"
                        >
                            {vcSaving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
                            Agregar comentario
                        </button>
                        {vcError && <span className="text-xs text-rose-500">{vcError}</span>}
                    </div>
                </div>

                {/* List */}
                {visitComments.length > 0 && (
                    <div className="space-y-2.5 pt-1">
                        {visitComments.map(vc => (
                            <div key={vc.id} className={clsx(
                                "group p-4 rounded-xl border flex items-start justify-between gap-3",
                                isLight ? "bg-slate-50 border-slate-100" : "bg-white/5 border-white/5"
                            )}>
                                <div className="min-w-0">
                                    <div className="flex items-center gap-1.5 text-xs font-bold text-slate-400 mb-1">
                                        <Calendar size={12} />
                                        {new Date(vc.visited_at).toLocaleString('es-GT', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                    </div>
                                    <p className={clsx("text-sm leading-relaxed break-words", isLight ? "text-slate-700" : "text-slate-300")}>{vc.comment}</p>
                                </div>
                                <button
                                    onClick={() => void handleDeleteComment(vc.id)}
                                    title="Eliminar comentario"
                                    className={clsx(
                                        "p-1.5 rounded-lg shrink-0 transition-colors",
                                        isLight ? "text-slate-400 hover:text-rose-600 hover:bg-rose-50" : "text-slate-500 hover:text-rose-400 hover:bg-rose-500/10"
                                    )}
                                >
                                    <Trash2 size={15} />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {isLoading ? (
                <div className="flex items-center justify-center py-16">
                    <Loader2 size={32} className="text-indigo-500 animate-spin" />
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Seguimientos timeline */}
                    <div className={clsx("lg:col-span-2 space-y-6", panelCls)}>
                        <div className="flex items-center gap-3">
                            <div className={clsx("p-2 rounded-lg", isLight ? "bg-indigo-50 text-indigo-600" : "bg-indigo-500/10 text-indigo-400")}>
                                <MessageSquare size={18} />
                            </div>
                            <h3 className={clsx("text-lg font-bold", isLight ? "text-slate-900" : "text-white")}>
                                Seguimientos ({seguimientos.length})
                            </h3>
                        </div>

                        {seguimientos.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-12 text-center">
                                <Clock size={32} className="text-slate-400 mb-3" />
                                <p className={clsx("text-sm font-medium", isLight ? "text-slate-500" : "text-slate-400")}>
                                    Sin seguimientos registrados para los contactos de esta propiedad
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {seguimientos.map((seg: any) => {
                                    const meta = TYPE_META[seg.type] || TYPE_META.other;
                                    return (
                                        <div key={seg.id} className={clsx(
                                            "p-4 rounded-xl border transition-all",
                                            isLight ? "bg-slate-50 border-slate-100" : "bg-white/5 border-white/5"
                                        )}>
                                            <div className="flex items-center justify-between mb-2">
                                                <div className="flex items-center gap-2">
                                                    <span className={clsx("px-2 py-0.5 rounded-md text-[10px] font-bold uppercase", meta.bg, meta.color)}>
                                                        {meta.label}
                                                    </span>
                                                    <span className={clsx("text-sm font-bold", isLight ? "text-slate-800" : "text-white")}>
                                                        {seg.contacts?.name || 'Contacto'}
                                                    </span>
                                                </div>
                                                <span className="text-xs text-slate-500">{timeAgo(seg.note_date)}</span>
                                            </div>
                                            {seg.note && (
                                                <p className={clsx("text-sm leading-relaxed", isLight ? "text-slate-600" : "text-slate-400")}>
                                                    "{seg.note}"
                                                </p>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Interested contacts sidebar */}
                    <div className={clsx("space-y-6", panelCls)}>
                        <div className="flex items-center gap-3">
                            <div className={clsx("p-2 rounded-lg", isLight ? "bg-indigo-50 text-indigo-600" : "bg-indigo-500/10 text-indigo-400")}>
                                <Users size={18} />
                            </div>
                            <h3 className={clsx("text-lg font-bold", isLight ? "text-slate-900" : "text-white")}>
                                Contactos ({contactProperties.length})
                            </h3>
                        </div>

                        {contactProperties.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-12 text-center">
                                <Users size={28} className="text-slate-400 mb-3" />
                                <p className={clsx("text-xs", isLight ? "text-slate-400" : "text-slate-500")}>
                                    Sin contactos vinculados a esta propiedad
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {contactProperties.map((cp: any) => {
                                    const outcomeMeta = OUTCOME_META[cp.outcome] || { label: cp.outcome || '—', color: 'text-slate-500' };
                                    return (
                                        <div key={cp.id} className={clsx(
                                            "p-4 rounded-xl border transition-all",
                                            isLight ? "bg-slate-50 border-slate-100 hover:border-slate-200" : "bg-white/5 border-white/5 hover:border-white/10"
                                        )}>
                                            <div className="flex items-center gap-3 mb-2">
                                                <div className={clsx(
                                                    "w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0",
                                                    isLight ? "bg-indigo-50 text-indigo-600" : "bg-indigo-500/10 text-indigo-400"
                                                )}>
                                                    {(cp.contacts?.name || 'C')[0].toUpperCase()}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className={clsx("text-sm font-bold truncate", isLight ? "text-slate-900" : "text-white")}>
                                                        {cp.contacts?.name || 'Contacto'}
                                                    </p>
                                                    <span className={clsx("text-[10px] font-bold uppercase", outcomeMeta.color)}>
                                                        {outcomeMeta.label}
                                                    </span>
                                                </div>
                                            </div>
                                            {cp.contacts?.phone && (
                                                <div className="flex items-center gap-2 text-xs text-slate-500">
                                                    <Phone size={11} /> {cp.contacts.phone}
                                                </div>
                                            )}
                                            {cp.notes && (
                                                <p className="text-xs text-slate-400 mt-2 italic">"{cp.notes}"</p>
                                            )}
                                            <p className="text-[10px] text-slate-400 mt-2">{timeAgo(cp.created_at)}</p>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
